import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import type { VisioRole } from '@jeraisonne/visio';

// `video_room_participants`/`join_visio_room_by_invite` sont postérieures aux
// types générés (comme `get_or_create_visio_room`, cf. CLAUDE.md) — client
// générique pour ces appels ; `supabase.auth.*` reste sur le client typé.
const db = supabase as unknown as SupabaseClient;

export type JoinResult =
  | { status: 'ok'; roomId: string; waiting: boolean }
  | { status: 'error'; message: string };

/**
 * Session côté navigateur : réutilise une session EXISTANTE (compte réel via
 * handoff antérieur, ou session anonyme d'une visite précédente du même
 * appareil) si présente, sinon crée une session anonyme (`signInAnonymously`)
 * — JAMAIS un vrai `anon` : cette session obtient un JWT `role: authenticated`
 * + `is_anonymous: true` (cf. migration 0258, durcissement dédié).
 */
export async function ensureGuestSession(
  displayName: string,
  /** Jeton CAPTCHA (Turnstile), demandé SEULEMENT si une session anonyme doit être créée. */
  getCaptchaToken?: () => Promise<string | undefined>,
): Promise<{ userId: string } | { error: string }> {
  const { data: existing, error: existingError } = await supabase.auth.getUser();
  if (existing.user) return { userId: existing.user.id };
  // Session locale PÉRIMÉE (compte anonyme purgé par le cron, migration 0260 :
  // le JWT désigne un utilisateur qui n'existe plus) → on l'efface localement
  // avant d'en recréer une, sinon supabase-js garderait le jeton mort.
  if (existingError) await supabase.auth.signOut({ scope: 'local' });

  const captchaToken = await getCaptchaToken?.();
  const { data, error } = await supabase.auth.signInAnonymously({
    options: { data: { display_name: displayName }, captchaToken },
  });
  if (error || !data.user) return { error: error?.message ?? 'signin_failed' };
  return { userId: data.user.id };
}

/** Rejoint la salle via son lien — `p_display_name` est repassé à CHAQUE appel (une session
 * réutilisée d'une visite précédente n'a pas de profil : sans ça le nom retomberait sur « Invité »).
 * `avatarPreset` = clé d'avatar d'invité (liste fermée, `guest-avatars.ts`) — stockée sur la ligne
 * de roster le temps de la réunion ; une clé inconnue est ignorée par le serveur (migration 0260). */
export async function joinRoomByInvite(
  token: string,
  displayName: string,
  avatarPreset?: string | null,
): Promise<JoinResult> {
  try {
    const { data, error } = await db.rpc('join_visio_room_by_invite', {
      p_token: token,
      p_display_name: displayName,
      p_avatar_preset: avatarPreset ?? null,
    });
    if (error) return { status: 'error', message: error.message };
    const row = (data as Array<{ room_id: string; status: string }> | null)?.[0];
    if (!row) return { status: 'error', message: 'invalid_invite' };
    return { status: 'ok', roomId: row.room_id, waiting: row.status === 'waiting' };
  } catch {
    return { status: 'error', message: 'network_error' };
  }
}

/**
 * Rôle/nom AUTHENTIQUES posés par le serveur (`join_visio_room_by_invite`
 * calcule `role` depuis `profiles.role`/l'identité hôte — jamais reconstitué
 * côté client) — self-select autorisé même en `status='waiting'`
 * (`video_room_participants: select_visible`, migration 0255).
 */
export async function fetchMyParticipant(
  roomId: string,
  userId: string,
): Promise<{ role: VisioRole; displayName: string } | null> {
  const { data, error } = await db
    .from('video_room_participants')
    .select('role, display_name')
    .eq('room_id', roomId)
    .eq('user_id', userId)
    .maybeSingle();
  if (error || !data) return null;
  return { role: data.role as VisioRole, displayName: data.display_name ?? '' };
}

/**
 * Suit le statut de SA PROPRE ligne (`waiting → admitted/denied/removed`) —
 * la salle d'attente n'a pas d'autre signal de sortie tant que l'hôte ne
 * tranche pas. Résilié via la fonction renvoyée.
 */
export function watchMyParticipantStatus(
  roomId: string,
  userId: string,
  onChange: (status: string) => void,
): () => void {
  const channel = db
    .channel(`invite-status:${roomId}:${userId}`)
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'video_room_participants',
        filter: `room_id=eq.${roomId}`,
      },
      (payload: { new: { user_id: string; status: string } }) => {
        if (payload.new.user_id === userId) onChange(payload.new.status);
      },
    )
    .subscribe();
  return () => {
    void channel.unsubscribe();
  };
}
